import CloudySun from '@/src/assets/svgs/CloudySun'
import { DarkModeOutlined, WbSunnyOutlined } from '@mui/icons-material'
import { SvgIconProps, SvgIconTypeMap } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'

type timeSlot = '0600' | '1200' | '1800' | '2000'
export const timeRangeMap: Record<timeSlot, { start: number; end: number }> = {
    '0600': {
        start: 0,
        end: 600
    },
    '1200': {
        start: 600,
        end: 1200
    },
    '1800': {
        start: 1200,
        end: 1800
    },
    '2000': {
        start: 1800,
        end: 2400
    }
}

export const timeToSlot: (time: number) => timeSlot | null = (time: number) => {
    if (time >= 0 && time <= 600) {
        return '0600'
    }
    if (time >= 600 && time <= 1200) {
        return '1200'
    }
    if (time >= 1200 && time <= 1800) {
        return '1800'
    }
    if (time >= 1800 && time <= 2400) {
        return '2000'
    }
    return null
}

export interface FlightTimingSlot {
    label: string
    subLabel: string
    intlLabel: string
    value: timeSlot
    time: {
        start: number
        end: number
    }
    icon:
        | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & { muiName: string })
        | ((props: SvgIconProps) => JSX.Element)
        | null
    isSelected?: boolean
}

const useFlightTimingFilterManager = (journeyLeg: 0 | 1 = 0) => {
    const updateCache = useUpdateSearchResultCache()
    const { filterData, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const tFilters = useTranslations('srpFilters')
    const { triggerSrpEvent } = useClevertapForSrp()
    const { departureTimeFrom } = filterData || {}
    const targetDepartureTime = departureTimeFrom?.[selectedJourneyLeg]
    const { selectedValues: selectedFlightTimings } = targetDepartureTime || {}

    const title = tFilters('timeFilterCta')
    const timeSlots: FlightTimingSlot[] = useMemo(
        () => [
            {
                label: tFilters('timeFilter1', { time: '6' }),
                subLabel: 'Midnight - 6 am',
                intlLabel: '12am_6am',
                value: '0600',
                time: timeRangeMap['0600'],
                icon: CloudySun,
                isSelected: selectedFlightTimings?.includes('0600')
            },
            {
                label: tFilters('timeFilter2', { time1: '6', time2: '12' }),
                subLabel: '6 am - Noon',
                intlLabel: '6am_12pm',
                value: '1200',
                time: timeRangeMap['1200'],
                icon: WbSunnyOutlined,
                isSelected: selectedFlightTimings?.includes('1200')
            },
            {
                label: tFilters('timeFilter3', { time1: '12', time2: '6' }),
                subLabel: 'Noon - 6 pm',
                intlLabel: '12pm_6pm',
                value: '1800',
                time: timeRangeMap['1800'],
                icon: CloudySun,
                isSelected: selectedFlightTimings?.includes('1800')
            },
            {
                label: tFilters('timeFilter4', { time: '6' }),
                subLabel: '6 pm - Midnight',
                intlLabel: '6pm_12am',
                value: '2000',
                time: timeRangeMap['2000'],
                icon: DarkModeOutlined,
                isSelected: selectedFlightTimings?.includes('2000')
            }
        ],
        [selectedFlightTimings]
    )

    const handleSelectToggle = useCallback(
        (id: string) => {
            const currentSelected = selectedFlightTimings || []
            const isRemoving = currentSelected.includes(id)
            const newSelected = isRemoving ? currentSelected.filter(s => s !== id) : [...currentSelected, id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'departure_time_onward',
                action_value: newSelected.join(',') || 'none'
            })

            updateCache(currentData => {
                if (currentData?.filterData?.departureTimeFrom?.[selectedJourneyLeg]) {
                    currentData.filterData.departureTimeFrom[selectedJourneyLeg].selectedValues =
                        currentData.filterData.departureTimeFrom[selectedJourneyLeg].selectedValues.includes(id)
                            ? currentData.filterData.departureTimeFrom[selectedJourneyLeg].selectedValues.filter(
                                  s => s !== id
                              )
                            : [...currentData.filterData.departureTimeFrom[selectedJourneyLeg].selectedValues, id]
                }
                return currentData as NonNullable<typeof currentData>
            })
        },
        [updateCache, selectedJourneyLeg, selectedFlightTimings, triggerSrpEvent]
    )

    const handleReset = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'departure_time_onward'
        })

        updateCache(currentData => {
            if (currentData?.filterData?.departureTimeFrom?.[selectedJourneyLeg]) {
                currentData.filterData.departureTimeFrom[selectedJourneyLeg].selectedValues = []
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [updateCache, selectedJourneyLeg, triggerSrpEvent])

    const shouldShowReset = useMemo(() => {
        return !!selectedFlightTimings?.length
    }, [selectedFlightTimings, selectedJourneyLeg])

    return {
        timeSlots,
        handleSelectToggle,
        handleReset,
        shouldShowReset,
        title
    }
}

export default useFlightTimingFilterManager
